// ----------------------------------------------------------------------------
// Client-side audio prep for Chord AI uploads.
// - Enforces a 1 hour duration cap (cheap metadata read, any file size).
// - Compresses large non-MP3 files in the browser by encoding a STEREO MP3
//   (decode -> 44.1 kHz stereo -> 192 kbps MP3). MP3 keeps the upload + stored
//   blob small while preserving the stereo image and full sample rate that
//   Demucs stem separation needs. (The old path downsampled to mono 22 kHz WAV,
//   which made the stored blob a .wav AND degraded separation quality.)
//   Already-MP3 files upload as-is; on any conversion failure we fall back to
//   the original file so the worker can still convert + separate at full quality.
// - The CPU-heavy PCM+LAME encode runs in a WEB WORKER
//   (chordAudioEncoder.worker.ts): on the main thread it froze the page for
//   large files (browser "page unresponsive" prompt). Decoding stays here
//   because AudioContext is unavailable in workers (it is async/native anyway).
// Throws Error with a user-friendly message on failure.
// ----------------------------------------------------------------------------

const MAX_DURATION_SECONDS = 60 * 60; // 1 hour
const CONVERT_OVER_BYTES = 60 * 1024 * 1024; // compress files larger than ~60 MB
const TARGET_SAMPLE_RATE = 44_100; // Demucs-native rate; preserve quality
// 320 kbps: effectively transparent. Browser conversion is the FIRST lossy
// generation in a chain (the worker re-encodes stems after separation), and
// starting at 192 made compounded artifacts audible in the final mix/stems.
const MP3_BITRATE_KBPS = 320;

type AudioContextCtor = typeof AudioContext;

function getAudioContextCtor(): AudioContextCtor | null {
  const w = window as unknown as {
    AudioContext?: AudioContextCtor;
    webkitAudioContext?: AudioContextCtor;
  };
  return w.AudioContext ?? w.webkitAudioContext ?? null;
}

/** Read just the duration via metadata, without decoding the whole file. */
export function getAudioDuration(file: File): Promise<number> {
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const audio = new Audio();
    audio.preload = 'metadata';
    audio.onloadedmetadata = () => {
      URL.revokeObjectURL(url);
      resolve(Number.isFinite(audio.duration) ? audio.duration : 0);
    };
    audio.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error('Could not read this audio file. Please try a different file.'));
    };
    audio.src = url;
  });
}

/** Run the CPU-heavy PCM+MP3 encode in the dedicated worker. */
function encodeMp3InWorker(
  left: Float32Array,
  right: Float32Array | null,
  sampleRate: number,
): Promise<Uint8Array> {
  return new Promise((resolve, reject) => {
    const worker = new Worker(new URL('./chordAudioEncoder.worker.ts', import.meta.url), {
      type: 'module',
    });
    worker.onmessage = (e: MessageEvent<{ ok: boolean; mp3?: Uint8Array; error?: string }>) => {
      worker.terminate();
      if (e.data.ok && e.data.mp3) resolve(e.data.mp3);
      else reject(new Error(e.data.error || 'MP3 encoding failed'));
    };
    worker.onerror = (e) => {
      worker.terminate();
      reject(new Error(e.message || 'MP3 encoding failed'));
    };
    const transfers: ArrayBuffer[] = [left.buffer as ArrayBuffer];
    if (right) transfers.push(right.buffer as ArrayBuffer);
    worker.postMessage({ left, right, sampleRate, bitrateKbps: MP3_BITRATE_KBPS }, transfers);
  });
}

/**
 * Compress an audio file to a stereo (or mono, if the source is mono) MP3 at
 * 44.1 kHz / 192 kbps. Preserving the stereo image + sample rate is what keeps
 * Demucs stem separation accurate, while MP3 keeps the upload + stored blob far
 * smaller than WAV. Throws on decode/encode failure so the caller can fall back
 * to uploading the original file untouched.
 */
async function convertToMp3(file: File): Promise<File> {
  const Ctx = getAudioContextCtor();
  if (!Ctx) {
    throw new Error('This browser cannot convert audio.');
  }

  const arrayBuffer = await file.arrayBuffer();

  // Decoding into a context at the target rate resamples big files down to
  // 44.1 kHz while decoding (keeps memory in check) WITHOUT collapsing to mono.
  const decodeCtx = new Ctx({ sampleRate: TARGET_SAMPLE_RATE });
  let decoded: AudioBuffer;
  try {
    decoded = await decodeCtx.decodeAudioData(arrayBuffer);
  } finally {
    void decodeCtx.close();
  }

  const channels = Math.min(2, decoded.numberOfChannels) || 1;
  const sampleRate = decoded.sampleRate;
  // Copy the channel data so the buffers can be TRANSFERRED to the worker.
  const left = new Float32Array(decoded.getChannelData(0));
  const right = channels === 2 ? new Float32Array(decoded.getChannelData(1)) : null;

  const merged = await encodeMp3InWorker(left, right, sampleRate);

  const baseName = file.name.replace(/\.[^/.]+$/, '') || 'song';
  return new File([merged.buffer as ArrayBuffer], `${baseName}.mp3`, { type: 'audio/mpeg' });
}

/**
 * Validate + prepare an audio file for Chord AI upload. Enforces the 1 hour cap
 * and shrinks large files client-side. Returns the file to upload (the original
 * when it is already small enough). Throws Error with a friendly message.
 */
export async function prepareAudioForUpload(file: File): Promise<File> {
  const duration = await getAudioDuration(file);
  if (duration > MAX_DURATION_SECONDS) {
    throw new Error('This song is longer than 1 hour. Please trim it and try again.');
  }
  // Already an MP3 -> upload as-is; it is already compact and the worker stores it.
  const isMp3 = file.type === 'audio/mpeg' || /\.mp3$/i.test(file.name);
  if (isMp3) return file;
  // Large non-MP3 -> compress to a stereo MP3 so the upload + stored blob stay
  // small WITHOUT the separation-killing quality loss of the old mono 22 kHz WAV
  // path. On any failure, fall back to the original so the worker can still
  // convert + separate at full quality.
  if (file.size > CONVERT_OVER_BYTES) {
    try {
      return await convertToMp3(file);
    } catch {
      return file;
    }
  }
  return file;
}
